import * as linkify from 'linkifyjs';
import { type ComponentProps, cloneElement, Fragment, isValidElement, type ReactNode } from 'react';

import { NEW_TAB } from '~/constants/urls';

export type LinkifyProps = {
  children: ReactNode;
  renderLink?: (props: ComponentProps<'a'>) => ReactNode;
};

/**
 * This component will parse its `children`, detect URL's and wrap them in an `a` tag.
 * Otherwise it renders whatever it was passed as `children`.
 */
const Linkify = ({ children, renderLink }: LinkifyProps) => {
  const parseString = (text: string) => {
    const matches = linkify.find(text);
    if (!matches || matches.length === 0) {
      return text;
    }

    const result: ReactNode[] = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
      // Add text before the link
      if (match.start > lastIndex) {
        result.push(text.slice(lastIndex, match.start));
      }

      // Add the link
      const linkProps: ComponentProps<'a'> = {
        href: match.href,
        target: NEW_TAB,
        rel: 'noopener noreferrer',
        onClick: (e) => {
          // we have to stop propagation in order for the `Description`'s `Button` onClick
          // handler not to trigger
          e.stopPropagation();
        },
        onFocus: (e) => {
          // we have to stop propagation in order for the `Description`'s `Button` onClick
          // handler not to trigger
          e.stopPropagation();
        },
        children: match.value,
      };

      result.push(
        renderLink ? <Fragment key={index}>{renderLink(linkProps)}</Fragment> : <a key={index} {...linkProps} />,
      );

      lastIndex = match.end;
    });

    // Add any remaining text
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result;
  };

  const parse = (children: ReactNode, key = 0): ReactNode => {
    if (typeof children === 'string') {
      return parseString(children);
    } else if (isValidElement(children) && children.type !== 'a' && children.type !== 'button') {
      return isValidElement(children)
        ? isValidElement(children.props.children)
          ? children
          : isValidElement(children.props.children)
            ? cloneElement(children, { key }, parse(children.props.children))
            : cloneElement(children, { key }, parse(children.props.children))
        : children;
    } else if (Array.isArray(children)) {
      return children.map((child, i) => parse(child, i));
    }

    return children;
  };

  return <>{parse(children)}</>;
};

export default Linkify;
